import React, { useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@glu/theming';
import styles from './Button.styles';
import registerDefaults from './themeDefaults';

export const PRIMARY = 'primary';
export const SECONDARY = 'secondary';
export const TERTIARY = 'tertiary';
export const NORMAL = 'normal';
export const SMALL = 'small';
export const EXTRA_SMALL = 'extraSmall';

registerDefaults();

let uniqueId = 0;

/**
 * Basic Glu button supporting the primary, secondary, and tertiary types.
 */
export const BareButton = ({
  component: Component,
  variant,
  type,
  size,
  className,
  autoFocus,
  text,
  children,
  onClick,
  classes,
  theme,
  dataQa,
  'aria-busy': ariaBusy,
  ...rest
}) => {
  const ref = useRef();
  const dataQAFallback = useMemo(() => `button-${uniqueId++}`, []); // eslint-disable-line no-plusplus

  useEffect(() => {
    if (autoFocus) {
      ref.current.focus();
    }
  }, [autoFocus]);

  return (
  /* eslint-disable-next-line react/button-has-type *//* trips up on non static type */
    <Component
      ref={ref}
      data-qa={dataQa || dataQAFallback}
      type={type}
      className={`${classes.root} ${classes[variant]} ${classes[size]} ${className}`}
      onClick={ariaBusy ? undefined : onClick}
      aria-busy={ariaBusy}
      {...rest}
    >
      {text || children}
    </Component>
  );
};

BareButton.propTypes = {
  /** The tag or component to render the button styling to  */
  component: PropTypes.elementType,

  /** The type of glu button you want */
  variant: PropTypes.oneOf([PRIMARY, SECONDARY, TERTIARY]),

  /** The html type attribute to set for this button if you don't want the default of 'button' */
  type: PropTypes.oneOf(['submit', 'reset', 'button']),

  /** The size of the glu button you want */
  size: PropTypes.oneOf([NORMAL, SMALL, EXTRA_SMALL]),

  /** CSS classname to apply to this component. */
  className: PropTypes.string,

  /** Automatically set focus on button. */
  autoFocus: PropTypes.bool,

  /** The text of the button for simple buttons, used in preference to children */
  text: PropTypes.string,

  /** Child components of the button for times where you need more than simple text */
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node, PropTypes.element]),

  /** function to call when the button is clicked */
  onClick: PropTypes.func,

  /* JSS Classes */
  classes: PropTypes.shape({
    root: PropTypes.string
  }).isRequired,

  /** JSS Theme */
  theme: PropTypes.shape({ buttons: PropTypes.shape({}).isRequired }).isRequired,

  /** Data-qa attribute for automated QA testing */
  dataQa: PropTypes.string,
  /** The normal aria-busy property */
  'aria-busy': PropTypes.bool
};

BareButton.defaultProps = {
  component: 'button',
  variant: PRIMARY,
  type: 'button',
  size: NORMAL,
  className: '',
  autoFocus: false,
  text: undefined,
  children: undefined,
  dataQa: '',
  onClick: /* istanbul ignore next */ () => null,
  'aria-busy': undefined
};
export const Button = withStyles(styles)(BareButton);
Button.displayName = 'Button';
